import { fetcher } from '@commenda/utils';
import { useState } from 'react';
import { BASE_URL } from './helpers';

export type PromoteTargetEnvironment = 'production' | 'demo';

export interface WorkflowPromoteLinkCutover {
  link: string;
  currentWorkflowKey: string | null;
  promotedWorkflowKey: string;
}

export interface WorkflowPromoteChangelogUpdate {
  workflowKey: string;
  version: string;
  from: string | null;
  to: string | null;
}

export interface WorkflowPromoteVersionConflict {
  workflowKey: string;
  version: string;
}

export interface WorkflowPromotePlan {
  createdWorkflows: string[];
  updatedWorkflows: string[];
  unchangedWorkflows: string[];
  createdForms: string[];
  updatedForms: string[];
  createdServices: string[];
  createdLinks: string[];
  repointedLinks: WorkflowPromoteLinkCutover[];
  versionConflicts: WorkflowPromoteVersionConflict[];
  changelogUpdates: WorkflowPromoteChangelogUpdate[];
}

export interface WorkflowPromoteResult {
  promotedWorkflows: string[];
  promotedForms: string[];
  promotedServices: string[];
  plan: WorkflowPromotePlan;
  dryRun: boolean;
}

export interface WorkflowPromoteConflict {
  message: string;
  repointedLinks?: WorkflowPromoteLinkCutover[];
  conflictingLinkIds?: string[];
  versionConflicts?: WorkflowPromoteVersionConflict[];
}

export const usePromoteWorkflowTemplate = () => {
  const [isPromoting, setIsPromoting] = useState(false);

  const promote = async (
    id: string,
    targetEnvironment: PromoteTargetEnvironment,
    repointLinks = false
  ): Promise<{
    success: boolean;
    data?: WorkflowPromoteResult;
    conflict?: WorkflowPromoteConflict;
    error?: string;
  }> => {
    setIsPromoting(true);
    try {
      const res = await fetcher(`${BASE_URL}/${id}/promote`, {
        method: 'POST',
        body: { targetEnvironment, repointLinks },
      });

      if (res.status === 409) {
        const body = (await res.json()) as WorkflowPromoteConflict;
        return { success: false, conflict: body };
      }

      if (!res.ok) {
        const err = await res.text();
        return { success: false, error: err };
      }

      const data = (await res.json()) as WorkflowPromoteResult;
      return { success: true, data };
    } catch (err) {
      console.error(err);
      return { success: false, error: 'Promote request failed' };
    } finally {
      setIsPromoting(false);
    }
  };

  return { promote, isPromoting };
};
